/* eslint-disable implicit-arrow-linebreak */
import React, { useState } from 'react';
import PropTypes from 'prop-types';
import {
  Button,
  Icon,
  Pane,
  PaneFooter,
  Tooltip,
} from '@folio/stripes/components';
import { FormattedMessage, useIntl } from 'react-intl';
import { useOkapiKy, IfPermission } from '@folio/stripes/core';
import { useQuery } from '@tanstack/react-query';
import ResultsActionsMenu from './ResultsActionsMenu';
import DisplayMethodsMenu from './DisplayMethodsMenu';
import displayMethods from '../../../../constants/DisplayMethods';
import GenericResultsDisplay from '../core/GenericResultsDisplay';
import DrillDownModal from './DrillDownModal';
import ExportProgressModal from './ExportProgressModal';
import ExportManager from '../core/ExportManager';
import ScheduleReportModal from '../../../scheduled/ScheduleReportModal';
import { getLocalizedValue } from '../../../../helpers/localizationUtils';

function ResultsPreview(props) {
  const intl = useIntl();
  const { locale } = intl;
  const okapiKy = useOkapiKy();
  const allDisplayMethods = displayMethods;
  const { resultSet, error, isLoading } = props;

  // Report context props
  const {
    reportName,
    reportDesc,
    reportId,
    isRunMode = false,
    isEditMode = false,
    hasChanges = false,
    onBuilderChange,
    onOpenSavePane,
    onUseQuery,
    savedDisplayMethods = [],
    isContextLoading = false,
  } = props;

  // In run mode: show only saved display methods
  // In edit mode or new report: show all methods
  const effectiveDisplayMethods =
    isRunMode && savedDisplayMethods.length > 0
      ? allDisplayMethods.filter((m) => savedDisplayMethods.includes(m.value))
      : allDisplayMethods;

  const filteredDataOptions = () =>
    effectiveDisplayMethods.map((option) => ({
      label: option.name,
      value: option.value,
    }));

  const { data: reportData } = useQuery({
    queryKey: ['report-chart-options', reportId],
    queryFn: () => okapiKy.get(`reports/${reportId}`).json(),
    enabled: !!reportId,
    staleTime: 5 * 60 * 1000,
  });
  const chartOptions = reportData?.visualization?.chartOptions || null;
  const formatting = reportData?.visualization?.formatting || null;
  const groupByDimension = reportData?.visualization?.groupByDimension || null;
  const pivotRowDimension =
    reportData?.visualization?.pivotRowDimension || null;
  const pivotColDimension =
    reportData?.visualization?.pivotColDimension || null;

  const resolvedReportName = reportName
    ? getLocalizedValue(reportName, locale)
    : null;
  const resolvedReportDesc = reportDesc
    ? getLocalizedValue(reportDesc, locale)
    : null;

  const [showDrillDownModal, setShowDrillDownModal] = useState(false);
  const [drillDownData, setDrillDownData] = useState(null);
  const [showExportModal, setShowExportModal] = useState(false);
  const [currentExportType, setCurrentExportType] = useState(null);
  const [showScheduleModal, setShowScheduleModal] = useState(false);

  const handleDrillDown = (data) => {
    if (data) {
      setDrillDownData(data);
      setShowDrillDownModal(true);
    }
  };

  const closeDrillDownModal = () => {
    setShowDrillDownModal(false);
    setDrillDownData(null);
  };

  const handleExportStart = (type) => {
    setCurrentExportType(
      type === 'excel' ? 'Excel' : type === 'pdf' ? 'PDF' : 'Print',
    );
    setShowExportModal(true);
  };

  const handleExportError = (type, exportError) => {
    console.error(`Export ${type} failed:`, exportError);
  };

  const closeExportModal = () => {
    setShowExportModal(false);
    setCurrentExportType(null);
  };

  const handleDownloadNow = (triggerDownload, pathInfo) => {
    if (triggerDownload) {
      closeExportModal();
      triggerDownload(pathInfo);
    }
  };

  const resultsCount =
    !isLoading && resultSet
      ? resultSet.serialize?.()?.loadResponse?.results?.[0]?.total ||
        resultSet?.loadResponses?.[0]?.total ||
        resultSet.tablePivot?.()?.length ||
        0
      : 0;

  const renderFirstMenu = () => (
    <>
      {!props.showQueryBuilderPane ? (
        <Tooltip
          id="report-preview-pane-tooltip-show-query-builder"
          text={
            <FormattedMessage
              id="ui-reports.newReport.reportPreviewPane.tooltip.showQueryBuilderPane"
              defaultMessage="Show query builder panel"
            />
          }
        >
          {({ ref, ariaIds }) => (
            <Button
              buttonStyle="slim"
              id="report-preview-pane-showQueryBuilder-btn"
              marginBottom0
              onClick={() =>
                props.setShowQueryBuilderPane(!props.showQueryBuilderPane)
              }
              aria-labelledby={ariaIds.text}
              ref={ref}
            >
              <Icon icon="chevron-double-right" size="medium" />
              {!isRunMode && (
                <FormattedMessage
                  id="ui-reports.queryFilters.showFilterBtn"
                  defaultMessage="Filters"
                />
              )}
            </Button>
          )}
        </Tooltip>
      ) : (
        <div />
      )}
      {!isLoading && resultSet && resultSet.loadResponses?.length > 0 && (
        <DisplayMethodsMenu
          displayMethods={filteredDataOptions()}
          currentDisplayMethod={props.displayMethod}
          setCurrentDisplayMethod={props.setDisplayMethod}
        />
      )}
    </>
  );

  const renderPaneFooter = () => {
    // In run/edit mode with a saved report
    const isReportMode = (isRunMode || isEditMode) && reportId;

    if (isReportMode) {
      return (
        <PaneFooter
          renderEnd={
            <>
              {isRunMode && reportId && (
                <Button
                  id="report-schedule-btn"
                  marginBottom0
                  onClick={() => setShowScheduleModal(true)}
                >
                  <FormattedMessage id="ui-reports.report.details.actions.scheduleBtn" />

                  {/* <Icon icon="calendar">
                    <FormattedMessage id="ui-reports.report.details.actions.scheduleBtn" />
                  </Icon> */}
                </Button>
              )}
              <Button
                id="clickable-closeNoSaveBtn-reports-"
                marginBottom0
                onClick={props.handleClose}
              >
                <FormattedMessage id="ui-reports.newReport.reportPreviewPane.closeBtn" />
              </Button>
            </>
          }
          renderStart={
            hasChanges ? (
              <>
                <IfPermission perm="ui-reports.reports.edit">
                  <Button
                    buttonStyle="primary"
                    id="report-update-current-btn"
                    marginBottom0
                    onClick={() =>
                      onOpenSavePane ? onOpenSavePane('update') : null
                    }
                    disabled={!resultSet || props.showSavePane}
                  >
                    <FormattedMessage
                      id="ui-reports.reportPreview.updateCurrentBtn"
                      defaultMessage="Update report"
                    />
                  </Button>
                </IfPermission>
                <IfPermission perm="ui-reports.reports.create">
                  <Button
                    buttonStyle="default"
                    id="report-save-as-new-btn"
                    marginBottom0
                    onClick={() =>
                      onOpenSavePane ? onOpenSavePane('new') : null
                    }
                    disabled={!resultSet || props.showSavePane}
                  >
                    <FormattedMessage
                      id="ui-reports.reportPreview.saveAsNewBtn"
                      defaultMessage="Save as new report"
                    />
                  </Button>
                </IfPermission>
              </>
            ) : null
          }
        />
      );
    }

    // Default mode (new report from builder)
    return (
      <PaneFooter
        renderEnd={
          <Button
            id="clickable-closeNoSaveBtn-reports-"
            marginBottom0
            onClick={props.handleClose}
            disabled={props.showSavePane}
          >
            <FormattedMessage
              id="ui-reports.newReport.reportPreviewPane.closeBtn"
              defaultMessage="Close"
            />
          </Button>
        }
        renderStart={
          <>
            {onUseQuery && (
              <Button
                buttonStyle="primary"
                id="widget-use-this-query-btn"
                marginBottom0
                disabled={!resultSet || isLoading}
                onClick={() => onUseQuery(props.query)}
              >
                <Icon icon="check">
                  <FormattedMessage
                    id="ui-reports.widget.useThisQuery"
                    defaultMessage="Use this Query"
                  />
                </Icon>
              </Button>
            )}
            {!onUseQuery && (
              <IfPermission perm="ui-reports.reports.create">
                <Button
                  buttonStyle="primary"
                  id="new-report-save-as-report-btn"
                  marginBottom0
                  onClick={() => {
                    if (onOpenSavePane) {
                      onOpenSavePane('new');
                    } else {
                      props.setShowQueryBuilderPane(false);
                      props.setShowSavePane(true);
                    }
                  }}
                  disabled={!resultSet || props.showSavePane}
                >
                  <FormattedMessage
                    id="ui-reports.newReport.reportPreviewPane.saveAsReportBtn"
                    defaultMessage="Save as report"
                  />
                </Button>
              </IfPermission>
            )}
          </>
        }
      />
    );
  };

  // Determine pane title and sub
  const paneTitle = resolvedReportName || (
    <FormattedMessage
      id="ui-reports.newReport.reportPreviewPane.paneTitle"
      defaultMessage="Results Preview"
    />
  );

  const paneSub = resolvedReportDesc ? (
    `${resolvedReportDesc} — ${intl.formatMessage(
      {
        id: 'stripes-smart-components.searchResultsCountHeader',
        defaultMessage: '{count} records found',
      },
      { count: resultsCount },
    )}`
  ) : (
    <FormattedMessage
      id="stripes-smart-components.searchResultsCountHeader"
      values={{ count: resultsCount }}
    />
  );

  return (
    <ExportManager
      query={props.query}
      reportId={reportId}
      reportName={resolvedReportName || `folio-report-${Date.now()}`}
      formats={['excel', 'pdf', 'print']}
      onExportStart={handleExportStart}
      onExportError={handleExportError}
      displayMethod={props.displayMethod}
      displayConfig={{
        groupByDimension,
        pivotRowDimension,
        pivotColDimension,
      }}
    >
      {({ exportFunctions, exportState, printOptions }) => (
        <Pane
          appIcon={<Icon icon="preview" size="large" />}
          fluidContentWidth
          noOverflow
          firstMenu={renderFirstMenu()}
          lastMenu={
            <ResultsActionsMenu
              handleExcelExport={exportFunctions.excel}
              handleCsvExport={exportFunctions.csv}
              toPDF={exportFunctions.pdf}
              handlePrint={exportFunctions.print}
            />
          }
          footer={renderPaneFooter()}
          id="pane-report-preview"
          centeredContent
          paneTitle={paneTitle}
          paneSub={paneSub}
        >
          <GenericResultsDisplay
            resultSet={resultSet}
            error={error}
            isLoading={isLoading}
            displayMethod={props.displayMethod}
            reportType={props.reportType}
            onDrillDown={handleDrillDown}
            query={props.query}
            setQuery={props.setQuery}
            printOptions={printOptions}
            exportOptions={{ excel: true, pdf: true }}
            chartOptions={chartOptions}
            formatting={formatting}
            groupByDimension={groupByDimension}
            pivotRowDimension={pivotRowDimension}
            pivotColDimension={pivotColDimension}
          />

          <DrillDownModal
            isOpen={showDrillDownModal}
            onClose={closeDrillDownModal}
            drillDownData={drillDownData}
          />

          <ExportProgressModal
            isOpen={showExportModal}
            exportType={currentExportType}
            exportStage={exportState.exportStage}
            exportError={exportState.exportError}
            exportProgress={exportState.exportProgress}
            onClose={closeExportModal}
            onConfirm={exportState.onConfirm}
            onRetry={closeExportModal}
            onDownload={() => exportFunctions.triggerDownload()}
            defaultFilename={`folio-report-${Date.now()}.xlsx`}
            supportsAsShown={exportState.supportsAsShown}
            exportAsShown={exportState.exportAsShown}
            setExportAsShown={exportState.setExportAsShown}
          />

          {showScheduleModal && reportId && (
            <ScheduleReportModal
              open={showScheduleModal}
              onClose={() => setShowScheduleModal(false)}
              reportId={reportId}
              reportName={resolvedReportName || ''}
            />
          )}
        </Pane>
      )}
    </ExportManager>
  );
}

ResultsPreview.propTypes = {
  handleClose: PropTypes.func.isRequired,
  setShowSavePane: PropTypes.func.isRequired,
  showSavePane: PropTypes.bool.isRequired,
  showQueryBuilderPane: PropTypes.bool.isRequired,
  setShowQueryBuilderPane: PropTypes.func.isRequired,
  query: PropTypes.oneOfType([PropTypes.string, PropTypes.object]).isRequired,
  setQuery: PropTypes.func,
  resultSet: PropTypes.object,
  error: PropTypes.object,
  isLoading: PropTypes.bool.isRequired,
  reportType: PropTypes.string.isRequired,
  displayMethod: PropTypes.string.isRequired,
  setDisplayMethod: PropTypes.func.isRequired,
  // Report context props
  reportName: PropTypes.oneOfType([PropTypes.string, PropTypes.array]),
  reportDesc: PropTypes.oneOfType([PropTypes.string, PropTypes.array]),
  reportId: PropTypes.string,
  isRunMode: PropTypes.bool,
  isEditMode: PropTypes.bool,
  hasChanges: PropTypes.bool,
  onBuilderChange: PropTypes.func,
  onOpenSavePane: PropTypes.func,
  onUseQuery: PropTypes.func,
  savedDisplayMethods: PropTypes.arrayOf(PropTypes.string),
  isContextLoading: PropTypes.bool,
};

export default ResultsPreview;
